import React, {useEffect, useState} from 'react';
import {Platform, StyleSheet, Text, View} from 'react-native';
import Typography from '@shared/constants/Typography';
import {palette} from '@shared/theme/colorGroups/palette.colors';
import {WAKE_WORD} from './wakeWordConfig';
import {
  readWakeWordDiagnostics,
  WakeWordDiagnostics,
} from './wakeWordDiagnostics';

/** How often the open panel reads what the engine noted. */
const REFRESH_MS = 250;

const clock = (at: number): string => {
  const time = new Date(at);
  return [time.getHours(), time.getMinutes(), time.getSeconds()]
    .map(part => String(part).padStart(2, '0'))
    .join(':');
};

const decibels = (value: number): string => `${Math.round(value)} dB`;

/**
 * Hey Nova's diagnostics, opened by a long press on "Hey Nova" in the chat
 * header: for testers finding out why a "Hey Nova" went unheard — the
 * microphone closed or reopening, or the phrase heard but scored under the
 * threshold. Not for technicians, so in English, untranslated.
 */
export const WakeWordDiagnosticsPanel = () => {
  const [now, setNow] = useState<WakeWordDiagnostics>(readWakeWordDiagnostics);
  useEffect(() => {
    const timer = setInterval(
      () => setNow(readWakeWordDiagnostics()),
      REFRESH_MS,
    );
    return () => clearInterval(timer);
  }, []);

  return (
    <View style={styles.panel} testID="wakeWordDiagnosticsId">
      <Text style={styles.line}>{`Engine: ${now.engine}`}</Text>
      <Text style={styles.line}>{`Mic: ${now.mic}`}</Text>
      <Text style={styles.line}>
        {now.scored
          ? `Score ${now.score.toFixed(2)} · best 10 s ${now.bestScore.toFixed(
              2,
            )} · needs ${WAKE_WORD.threshold.toFixed(2)} ×${
              WAKE_WORD.patience
            } in a row`
          : 'Score: not reported — this engine says only that it heard'}
      </Text>
      <Text style={styles.line}>
        {`Level ${decibels(now.level)} · loudest 10 s ${decibels(
          now.loudest,
        )}`}
      </Text>
      <Text style={styles.line}>{`Audio: ${now.audio || 'not reported'}`}</Text>
      <Text style={styles.line}>
        {`Wakes ${now.wakes} · fell behind ${now.fellBehind} · failed openings ${now.reopenFailures}`}
      </Text>
      {now.events.map((event, index) => (
        <Text key={`${event.at}-${index}`} style={styles.event}>
          {`${clock(event.at)}  ${event.text}`}
        </Text>
      ))}
    </View>
  );
};

// Light on dark in either theme: a tester's readout, set apart from the chat.
const monospace = Platform.select({ios: 'Menlo', default: 'monospace'});
const styles = StyleSheet.create({
  panel: {
    marginHorizontal: Typography.margin.margin12,
    marginBottom: Typography.margin.margin8,
    paddingHorizontal: Typography.padding.padding10,
    paddingVertical: Typography.padding.padding8,
    borderRadius: Typography.radius.radius8,
    backgroundColor: palette.gray.darkest,
  },
  line: {
    fontFamily: monospace,
    fontSize: Typography.fontSize.fontSize11,
    lineHeight: Typography.lineHeight.lineHeight16,
    color: palette.gray.white,
  },
  event: {
    fontFamily: monospace,
    fontSize: Typography.fontSize.fontSize11,
    lineHeight: Typography.lineHeight.lineHeight16,
    color: palette.gray.medium,
  },
});
